import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Dropdown, Input, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { ChevronDown, CircleDollarSign, Coins, Eye, Search } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { CardMenu } from '@/components/CardMenu';
import { InterviewTemplatesService, BaseAPIClient } from '@/services/api';
import type { InterviewTemplate } from '@/services/api';
import { UserService } from '@/services/userService';
import { ROUTES } from '@/routes';
import { APP_CURRENCY } from '@/utils/constants/currency';
import { buildPackageBuyUrl } from '@/utils/buyLink';
import { useUserStore, toStoreUser } from '@/store/userStore';
import { useSessionWizardStore } from '@/store';
import { AnswerMode } from '@/store/interviewStore';
import { autoSelectPart } from '@/pages/Sessions/interviewStatus';
import { PackageDetailsDrawer } from './PackageDetailsDrawer';
import styles from './Packages.module.scss';

const { Title, Text } = Typography;

interface OwnedPackage {
  packageId: string;
  count: number;
  template: InterviewTemplate;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' as const } },
};

const PACKAGE_PAGE_SIZE = 12;

export const Packages: React.FC = () => {
  const { getToken } = useAuth();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const balance = useUserStore((state) => state.user?.balance);
  const setUser = useUserStore((state) => state.setUser);
  const setWizard = useSessionWizardStore((s) => s.setWizard);

  const [ownedPackages, setOwnedPackages] = useState<OwnedPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewingTemplate, setViewingTemplate] = useState<InterviewTemplate | null>(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | 'comp' | 'hts'>('all');
  const [limit, setLimit] = useState(PACKAGE_PAGE_SIZE);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const [templates] = await Promise.all([
        new InterviewTemplatesService(token).list(),
        new UserService(token)
          .getMe()
          .then((backendUser) => setUser(toStoreUser(backendUser)))
          .catch((error) => console.warn('Failed to refresh credit balance', error)),
      ]);

      const byPackage = new Map<string, OwnedPackage>();
      for (const t of templates) {
        if (!t.sourcePackageId) continue;
        const existing = byPackage.get(t.sourcePackageId);
        if (!existing) {
          byPackage.set(t.sourcePackageId, { packageId: t.sourcePackageId, count: 1, template: t });
        } else {
          existing.count += 1;
          if (new Date(t.purchasedAt) > new Date(existing.template.purchasedAt)) {
            existing.template = t;
          }
        }
      }
      setOwnedPackages(Array.from(byPackage.values()));
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getToken, notification, setUser]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Mirrors Sessions' "Try again": preselect the template and land on the
  // wizard's first step, letting the user pick part/mode themselves.
  const practiceTemplate = (template: InterviewTemplate) => {
    const part = autoSelectPart(template);
    setWizard((prev) => ({
      templateId: template.id,
      part,
      mode: part === 'comp' ? AnswerMode.Text : null,
      step: 0,
      flashNonce: prev.flashNonce + 1,
    }));
    navigate(ROUTES.SESSIONS);
  };

  const countOfKind = (isHts: boolean) => ownedPackages.filter((p) => p.template.htsRequired === isHts).length;

  const trimmedSearch = search.trim();
  const filteredPackages = ownedPackages.filter((p) => {
    if (trimmedSearch) return p.template.name.toLowerCase().includes(trimmedSearch.toLowerCase());
    if (kindFilter !== 'all') return p.template.htsRequired === (kindFilter === 'hts');
    return true;
  });
  const visiblePackages = filteredPackages.slice(0, limit);
  const remainingCount = filteredPackages.length - visiblePackages.length;

  return (
    <motion.div className={styles.page} variants={containerVariants} initial="hidden" animate="visible">
      <div className={styles.pageHeadRow}>
        <motion.div className={styles.pageHead} variants={itemVariants}>
          <Title level={2} className={styles.pageTitle}>
            Your assessment packages
          </Title>
          <Text className={styles.pageSub}>Continue practicing with a package you&rsquo;ve unlocked.</Text>
        </motion.div>

        {typeof balance === 'number' && (
          <motion.div className={styles.creditBalance} variants={itemVariants}>
            <div className={styles.creditBalanceIcon}>
              <Coins style={{ width: '1em', height: '1em' }} />
            </div>
            <div>
              <div className={styles.creditBalanceLabel}>Your Balance</div>
              <div className={styles.creditBalanceValue}>
                <span className={styles.creditBalanceNumber}>{balance.toLocaleString()}</span>
                <span className={styles.creditBalanceUnit}>credits</span>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {ownedPackages.length > 0 && (
        <motion.div className={styles.searchFilterRow} variants={itemVariants}>
          <label className={styles.find}>
            <Search className={styles.findIcon} style={{ width: '1em', height: '1em' }} />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setLimit(PACKAGE_PAGE_SIZE);
              }}
              placeholder="Search packages"
              aria-label="Search packages"
              className={styles.findInput}
              variant="borderless"
            />
          </label>
          <Dropdown
            trigger={['click']}
            menu={{
              selectedKeys: [kindFilter],
              onClick: ({ key }) => {
                setKindFilter(key as 'all' | 'comp' | 'hts');
                setLimit(PACKAGE_PAGE_SIZE);
              },
              items: [
                { key: 'all', label: `All types ${ownedPackages.length}` },
                { key: 'comp', label: `Competency ${countOfKind(false)}` },
                { key: 'hts', label: `HTS ${countOfKind(true)}` },
              ] satisfies MenuProps['items'],
            }}
          >
            <button type="button" className={styles.kindDropdownTrigger}>
              {kindFilter === 'all' ? 'All types' : kindFilter === 'comp' ? 'Competency' : 'HTS'}
              <ChevronDown className={styles.kindDropdownIcon} style={{ width: '1em', height: '1em' }} />
            </button>
          </Dropdown>
        </motion.div>
      )}

      <motion.div variants={itemVariants}>
        {!loading && ownedPackages.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyText}>
              No packages purchased yet — ask your agency for a purchase link to unlock your first one.
            </div>
          </div>
        ) : !loading && filteredPackages.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyText}>
              {trimmedSearch ? `Nothing matches "${trimmedSearch}".` : 'Nothing in this filter.'}
            </div>
          </div>
        ) : (
          <div className={styles.tierGrid}>
            {visiblePackages.map(({ packageId, template }) => (
              <div key={packageId} className={`${styles.tier} ${styles.tierUnlocked}`}>
                <div className={styles.tierTopRow}>
                  <div className={styles.tierTitleGroup}>
                    <span className={styles.tierName} title={template.name}>
                      {template.name}
                    </span>
                    {template.htsRequired && <span className={styles.htsCorner}>+HTS</span>}
                  </div>
                  <CardMenu
                    ariaLabel={`More actions for ${template.name}`}
                    items={[
                      {
                        key: 'view',
                        label: 'View details',
                        icon: <Eye style={{ width: '1em', height: '1em' }} />,
                        onClick: () => setViewingTemplate(template),
                      },
                      {
                        key: 'buy',
                        label: 'Buy it again',
                        icon: <CircleDollarSign style={{ width: '1em', height: '1em' }} />,
                        onClick: () => {
                          window.location.href = buildPackageBuyUrl(packageId);
                        },
                      },
                    ]}
                  />
                </div>

                <div className={styles.tierPrice}>
                  <span className={styles.tierPriceDollarSign}>$</span>
                  <span className={styles.tierPriceNumber}>{(template.unlockPriceCents / 100).toFixed(2)}</span>
                  <span className={styles.tierPriceUnit}>{APP_CURRENCY}</span>
                </div>

                <div className={styles.tierDesc}>{template.description || 'Practice package'}</div>

                <div className={styles.tierSpacer} />

                <Button
                  appearance="solid"
                  className={styles.practiceButton}
                  onClick={() => practiceTemplate(template)}
                >
                  Practice
                </Button>
              </div>
            ))}
          </div>
        )}
        {remainingCount > 0 && (
          <button
            type="button"
            className={styles.showMore}
            onClick={() => setLimit((n) => n + PACKAGE_PAGE_SIZE)}
          >
            Show more ({remainingCount} left)
          </button>
        )}
      </motion.div>

      <PackageDetailsDrawer
        template={viewingTemplate}
        onClose={() => setViewingTemplate(null)}
        onPractice={() => {
          if (viewingTemplate) practiceTemplate(viewingTemplate);
          setViewingTemplate(null);
        }}
      />
    </motion.div>
  );
};

export default Packages;
